<template>
	<div :style="[getStyle]" class="pie">
		<div class="pie-segment" :class="{overload: isOverloading}"/>
	</div>
</template>

<script>
import iconMixin from './iconMixin';
export default {
	name: 'TPieIcon',
	mixins: [iconMixin],
	props: {
		/**
		 * Value of pie-segment fill.
		 */
		value: {
			type: [ Number, String ],
			default: 0,
		},
		/**
		 * Max value.
		 */
		max: [ Number, String ],
		/**
		 * Min value.
		 */
		min: {
			type: [ Number, String ],
			default: 0,
		}
	},
	computed: {
		parsedValue() {
			return parseFloat(this.value);
		},
		parsedMin() {
			return parseFloat(this.min);
		},
		parsedMax() {
			return parseFloat(this.max);
		},
		percentageValue() {
			if (!this.max) return 0;
			return Math.min(100, ((this.parsedValue / (this.parsedMax - this.parsedMin)) - (this.parsedMin / (this.parsedMax - this.parsedMin))) * 100);
		},
		isOverloading() {
			return this.parsedValue > this.parsedMax;
		},
		getStyle() {
			return {
				'--value': this.percentageValue,
				'--over50': (this.percentageValue > 50 ? 1 : 0),
				...this.getSize
			};
		},
	},
	created() {
		if (this.min > this.max) {
			throw new Error('Min value can not be equal or greater than Max value');
		}
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
.pie {
	position: relative;
	height: 1em;
	width: 1em;
	border-radius: 100%;
	background-color: get-color($icon, 'disabled');
	overflow: hidden;
}

.pie-segment {
	height: 100%;
	width: 100%;
	position: absolute;
	text-align: left; // Text alignment changes transform values, force circle left aligned
	transform: translate(0, -50%) rotate(90deg) rotate(0);
	transform-origin: 50% 100%;
	$a: calc(var(--over50, 0) * -100%);
	$b: calc((1 + var(--over50, 0)) * 100%);
	clip-path: polygon($a $a, $b $a, $b $b, $a $b);
	&:before, &:after {
		content: '';
		height: 100%;
		width: 100%;
		background-color: get-color($blue, 'base');
		position: absolute;
	}
	&.overload {
		&:before, &:after {
			background-color: get-color($orange, 'base');
		}
	}
	&:before {
		--degrees: calc((var(--value, 45) / 100) * 360);
		transform: translate(0, 100%) rotate(calc(var(--degrees) * 1deg));
		transform-origin: 50% 0;
	}
	&:after {
		opacity: var(--over50, 0);
	}
}
</style>

<docs>
```html
<t-pie-icon size="medium" :value="33"/>
<t-pie-icon size="large" :value="33" :min="22" :max="44"/>
```

```jsx noeditor
new Vue({
	data() {
		return {
			count: 0,
			timerId: undefined
		}
	},
	methods: {
		onMouseOver() {
			if(this.timerId) return;
			this.timerId = setInterval(() => {
				if(this.count >= 100) {
					this.count = 0;
				} else {
					this.count++;
				}
			}, 50);
		},
		onMouseOut() {
			this.count = 0;
			clearInterval(this.timerId)
			this.timerId = null;
		}
	},
	template: `
	<div style="display: grid; grid-column-gap: 4px; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));">
		<t-pane style="flex-direction: column; width: 150px;" background="tint1" height="100px" center>
			<t-pie-icon size="small" :value="10"/>
			<span>small | 10</span>
		</t-pane>
		<t-pane style="flex-direction: column; width: 150px;" background="tint1" height="100px" center>
			<t-pie-icon size="medium" :value="33"/>
			<span>medium | 33</span>
		</t-pane>
		<t-pane style="flex-direction: column; width: 150px;" background="tint1" height="100px" center>
			<t-pie-icon size="medium" :value="33" :min="22" :max="44"/>
			<span>medium | 33 | 22 | 44</span>
		</t-pane>
		<t-pane style="flex-direction: column; width: 150px;" background="tint1" height="100px" center>
			<t-pie-icon size="large" :value="60"/>
			<span>large | 60</span>
		</t-pane>
		<t-pane style="flex-direction: column; width: 150px;" background="tint1" height="100px" center>
			<t-pie-icon size="large" :value="33" :min="22" :max="44"/>
			<span>large | 33, 22, 44</span>
		</t-pane>
		<t-pane style="flex-direction: column; width: 150px;" background="tint1" height="100px" center>
			<t-pie-icon size="large" :value="count" @mouseover.native="onMouseOver" @mouseout.native="onMouseOut"/>
			<span>large | {{count}}</span>
		</t-pane>
	</div>
	`
});

```
</docs>
